import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { downloadBlob, NotificationService } from '../../../../../common';
import { MasterDataDownloadFile, MasterDataFileInitialState, MasterDataUploadFile } from './master-data-file.actions';
import { MasterDataFileService } from './master-data-file.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface MasterDataFileStateModel {
  loaded: boolean;
  file: string | ArrayBuffer;
}

const initialState: MasterDataFileStateModel = {
  loaded: false,
  file: null,
};

@State<MasterDataFileStateModel>({
  name: 'masterDataFile',
  defaults: initialState,
})
@Injectable()
export class MasterDataFileState {
  constructor(public masterDataFileService: MasterDataFileService, private notificationService: NotificationService) {}

  @Selector()
  static getState(state: MasterDataFileStateModel): MasterDataFileStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: MasterDataFileStateModel): boolean {
    return state.loaded;
  }

  @Action(MasterDataDownloadFile)
  downloadFile(
    ctx: StateContext<MasterDataFileStateModel>,
    { payload }: MasterDataDownloadFile,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Blob> {
    return this.masterDataFileService.downloadDocument(payload).pipe(
      take(1),
      map((response: HttpResponse<Blob>) => {
        const fileName: string = response.headers.get('content-disposition').split('filename=')[1];

        downloadBlob(response.body, fileName);

        return response.body;
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(MasterDataUploadFile)
  uploadFile(
    ctx: StateContext<MasterDataFileStateModel>,
    { payload }: MasterDataUploadFile,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<MatSnackBarRef<SimpleSnackBar>>> {
    ctx.patchState({ loaded: false });

    return this.masterDataFileService.uploadDocument(payload.type, payload.file).pipe(
      take(1),
      map((file: string) => {
        ctx.patchState({ loaded: true, file });
        return of(this.notificationService.success('administration.addressManagement.successUpload'));
      }),
      catchError(({ error }: HttpErrorResponse) => {
        ctx.patchState({ loaded: false, file: null });
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(MasterDataFileInitialState)
  setInitialState(ctx: StateContext<MasterDataFileInitialState>): void {
    ctx.setState(initialState);
  }
}
